import React from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FileText, Inbox, Users, Package, Calculator, Download as DownloadIcon, Database, Mail, FileSignature, Gift, LogOut } from "lucide-react";

import AdminDashboard from "./AdminDashboard";
import AdminIntakes from "./AdminIntakes";
import AdminContacts from "./AdminContacts";
import AdminUsers from "./AdminUsers";
import AdminVorrats from "./AdminVorrats";
import AdminCalculator from "./AdminCalculator";
import AdminDownloads from "./AdminDownloads";
import AdminAbaNinja from "./AdminAbaNinja";
import AdminEmail from "./AdminEmail";
import AdminContracts from "./AdminContracts";
import AdminReferrals from "./AdminReferrals";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="pt-32 text-center">Lade…</div>;
  if (!user || !user.id) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;

  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="admin-layout">
      <div className="mbw-container py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-28">
            <div className="flex items-center gap-3 pb-5 border-b border-slate-200">
              <div className="w-10 h-10 rounded-full bg-[#991B1B] flex items-center justify-center text-white font-heading font-bold text-sm">
                A
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-[#991B1B] font-semibold">Admin</div>
                <div className="font-semibold text-[#1A2433] truncate text-sm">{user.email}</div>
              </div>
            </div>
            <nav className="mt-4 flex flex-col gap-1">
              <SideLink to="/admin" end icon={LayoutDashboard} label="Dashboard" />
              <SideLink to="/admin/anfragen" icon={FileText} label="Onboarding" />
              <SideLink to="/admin/kontakt" icon={Inbox} label="Kontakt-Inbox" />
              <SideLink to="/admin/kunden" icon={Users} label="Kunden" />
              <SideLink to="/admin/vorrats" icon={Package} label="Vorratsgesellschaften" />
              <SideLink to="/admin/kalkulator" icon={Calculator} label="Kalkulator-Konfig" />
              <SideLink to="/admin/downloads" icon={DownloadIcon} label="Downloads" />
              <SideLink to="/admin/vertraege" icon={FileSignature} label="Verträge" />
              <SideLink to="/admin/empfehlungen" icon={Gift} label="Empfehlungen" />
              <SideLink to="/admin/abaninja" icon={Database} label="AbaNinja" />
              <SideLink to="/admin/email" icon={Mail} label="E-Mail" />
              <button
                onClick={logout}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg mt-4"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            </nav>
          </div>
        </aside>

        <main className="lg:col-span-9">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="anfragen" element={<AdminIntakes />} />
            <Route path="kontakt" element={<AdminContacts />} />
            <Route path="kunden" element={<AdminUsers />} />
            <Route path="vorrats" element={<AdminVorrats />} />
            <Route path="kalkulator" element={<AdminCalculator />} />
            <Route path="downloads" element={<AdminDownloads />} />
            <Route path="vertraege" element={<AdminContracts />} />
            <Route path="empfehlungen" element={<AdminReferrals />} />
            <Route path="abaninja" element={<AdminAbaNinja />} />
            <Route path="email" element={<AdminEmail />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function SideLink({ to, end, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
          isActive ? "bg-[#1A2433] text-white" : "text-slate-700 hover:bg-slate-100"
        }`
      }
      data-testid={`admin-link-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}
